import { apiClient } from "@/lib/api-client"

export type CommentableEntity = "community" | "ritual"

const entityPathSegment: Record<CommentableEntity, string> = {
  community: "communities",
  ritual: "rituals",
}

interface ProfileMediaItem {
  isCover: boolean
  upload?: {
    id: string
    filePath: string
  }
}

export interface CommentUser {
  id: string
  name: string
  username: string
  profilePictureUrl?: string | null
  profileMedia?: ProfileMediaItem[]
}

export interface MentionUser {
  id?: string
  username: string
  name?: string
}

export interface Comment {
  id: string
  content: string
  userId: string
  user: CommentUser
  model: CommentableEntity
  modelId: string
  parentId: string | null
  threadId: string | null
  depthLevel: number
  isPinned: boolean
  isEdited: boolean
  isHidden?: boolean
  deleted: boolean
  createdAt: string
  updatedAt: string
  replies?: Comment[]
  mentions?: MentionUser[]
}

export interface PaginatedComments {
  data: Comment[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

export interface CreateCommentParams {
  content: string
  parentId?: string
  /** Ritual organizers only: also email + notify all members (top-level posts). */
  notifyMembers?: boolean
}

export interface UpdateCommentParams {
  content: string
}

export async function fetchComments(
  entityType: CommentableEntity,
  entityId: string,
  options?: {
    limit?: number
    offset?: number
    sort?: "newest" | "oldest" | "pinned"
    search?: string
  },
): Promise<PaginatedComments> {
  const params = new URLSearchParams()
  if (options?.limit !== undefined) params.append("limit", String(options.limit))
  if (options?.offset !== undefined) params.append("offset", String(options.offset))
  if (options?.sort) params.append("sort", options.sort)
  if (options?.search) params.append("search", options.search)

  const suffix = params.toString() ? `?${params.toString()}` : ""
  const response = await apiClient.get<{ data: PaginatedComments }>(
    `/api/${entityPathSegment[entityType]}/${entityId}/comments${suffix}`,
  )

  return response.data.data
}

export async function createComment(
  entityType: CommentableEntity,
  entityId: string,
  params: CreateCommentParams,
): Promise<Comment> {
  const response = await apiClient.post<{ data: Comment }>(
    `/api/${entityPathSegment[entityType]}/${entityId}/comments`,
    params,
  )

  return response.data.data
}

export async function updateComment(
  commentId: string,
  params: UpdateCommentParams,
): Promise<Comment> {
  const response = await apiClient.put<{ data: Comment }>(`/api/comments/${commentId}`, params)
  return response.data.data
}

export async function deleteComment(commentId: string): Promise<void> {
  await apiClient.delete(`/api/comments/${commentId}`)
}

export async function togglePinComment(commentId: string): Promise<Comment> {
  const response = await apiClient.post<{ data: Comment }>(`/api/comments/${commentId}/pin`)
  return response.data.data
}

export async function toggleHideReplies(commentId: string): Promise<Comment> {
  const response = await apiClient.post<{ data: Comment }>(
    `/api/comments/${commentId}/hide-replies`,
  )
  return response.data.data
}
